import React, { useCallback, useEffect, useState } from "react";
import { Plus, QrCode } from "lucide-react";
import { api } from "../api/client";
import type { Inspection, Operator, Paged, Part } from "../api/types";
import { Button, PageHeader, Toggle } from "../components/Controls";
import { Badge, DemoTag } from "../components/Badge";
import { Column, Table } from "../components/Table";
import { EmptyState } from "../components/EmptyState";
import { fmtDateTime } from "../lib/format";
import { OperatorFormModal, QrModal } from "./OperatorFormModal";

async function mapLimit<T>(items: T[], limit: number, fn: (x: T) => Promise<void>) {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (i < items.length) await fn(items[i++]);
  });
  await Promise.all(workers);
}

export default function OperatorsPage() {
  const [ops, setOps] = useState<Operator[]>([]);
  const [parts, setParts] = useState<Part[]>([]);
  const [last, setLast] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Operator | null | undefined>(undefined);
  const [qr, setQr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.get<Operator[]>("/operators", { active: showInactive ? "all" : "true" });
      setOps(list);
      setLoading(false);
      await mapLimit(list, 5, async (o) => {
        try {
          const r = await api.get<Paged<Inspection>>("/inspections", { operator_id: o.id, limit: 1 });
          setLast((m) => ({ ...m, [o.id]: r.items[0]?.timestamp || null }));
        } catch {
          setLast((m) => ({ ...m, [o.id]: null }));
        }
      });
    } catch {
      setLoading(false);
    }
  }, [showInactive]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get<Part[]>("/parts", { active: "true" }).then(setParts).catch(() => undefined);
  }, []);

  const columns: Column<Operator>[] = [
    { key: "name", header: "Nombre", render: (o) => <span className="inline-flex items-center gap-2 font-medium">{o.name}{o.is_demo && <DemoTag />}</span> },
    { key: "user", header: "Usuario", render: (o) => <span className="font-mono text-xs">{o.username}</span> },
    { key: "role", header: "Rol", render: (o) => (o.role === "admin" ? "Administrador" : "Operario") },
    {
      key: "badge", header: "Gafete", render: (o) => o.badge_code ? (
        <button className="inline-flex items-center gap-1 font-mono text-xs hover:text-signal" onClick={(e) => { e.stopPropagation(); setQr(o.badge_code); }} data-testid="operator-badge-qr-button"><QrCode className="h-3.5 w-3.5" />{o.badge_code}</button>
      ) : <span className="text-slate-400">—</span>,
    },
    { key: "part", header: "Pieza asignada", render: (o) => (o.assigned_part ? <span className="font-mono">{o.assigned_part.part_number}</span> : <span className="text-slate-400">—</span>) },
    { key: "st", header: "Estado", render: (o) => <Badge tone={o.active ? "ink" : "muted"}>{o.active ? "Activo" : "Inactivo"}</Badge> },
    { key: "last", header: "Última inspección", render: (o) => <span className="text-xs text-slate-500">{o.id in last ? fmtDateTime(last[o.id]) : "…"}</span> },
  ];

  return (
    <div className="animate-rise">
      <PageHeader title="Operarios" subtitle="Usuarios, gafetes y pieza asignada." actions={<Button onClick={() => setEditing(null)} data-testid="new-operator-button"><Plus className="h-4 w-4" /> Nuevo operario</Button>} />
      <div className="mb-4"><Toggle checked={showInactive} onChange={setShowInactive} label="Mostrar inactivos" testId="operators-show-inactive-toggle" /></div>
      <Table testId="operators-table" columns={columns} rows={ops} loading={loading} onRowClick={(o) => setEditing(o)} empty={<EmptyState title="Sin operarios" text="Crear un operario con su usuario, PIN y gafete." />} />
      <OperatorFormModal open={editing !== undefined} op={editing} parts={parts} onClose={() => setEditing(undefined)} onSaved={load} />
      <QrModal code={qr} onClose={() => setQr(null)} />
    </div>
  );
}
